import React from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AuthImg } from "@/lib/authMedia";
import { PHOTO_UPLOAD_ACCEPT } from "@/lib/photo-prep";
import { isAnimatedInProgress, isPictureInProgress, pictureStateOf, type FaceLockAvatar } from "@/lib/facelock";

// Pieces the FaceLock dialogs and cards share: the photo tray (one big Upload
// button, thumbnails, remove), the owner-approved photo guidance (plan, OWNER
// DECISIONS 2026-09-29; its first line updated for one set of 16-24 photos,
// 2026-09-30), and an avatar's picture.
//
// THE PICTURE (owner rule, 2026-09-30, final): an avatar's picture is ALWAYS a
// portrait drawn with the avatar itself, so its owner sees what it actually
// produces. An uploaded photo is never shown in its place, and neither is a
// letter: while there is none, a neutral silhouette that says why.

/** A neutral head-and-shoulders silhouette: the stand-in while an avatar has no portrait. */
export function AvatarSilhouette({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg viewBox="0 0 60 80" aria-hidden="true" focusable="false" className={className} style={style} data-testid="facelock-silhouette">
      <circle cx="30" cy="27" r="13" fill="currentColor" />
      <path d="M5 80c0-16 11.2-27 25-27s25 11 25 27z" fill="currentColor" />
    </svg>
  );
}

const PICTURE_CAPTION: Record<string, string> = {
  drawing: "faceLock.picture.drawing",
  learning: "faceLock.picture.learning",
  waiting: "faceLock.picture.waiting",
  failed: "faceLock.picture.failed",
};

/** The card's picture: the portrait drawn with the avatar itself, or a silhouette that says why there is none yet. */
export function FaceLockPortrait({ avatar }: { avatar: FaceLockAvatar }) {
  const { t } = useTranslation();
  const state = pictureStateOf(avatar);
  if (avatar.picture_url) {
    return (
      <AuthImg
        src={avatar.picture_url}
        alt={t("faceLock.pictureAlt", { name: avatar.name })}
        loading="lazy"
        data-testid={`facelock-portrait-${avatar.id}`}
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />
    );
  }
  const caption = PICTURE_CAPTION[state] ? t(PICTURE_CAPTION[state]) : "";
  return (
    <div
      data-testid={`facelock-picture-${avatar.id}`}
      data-picture-state={state}
      className="flex h-full flex-col items-center justify-center gap-2 px-3 pb-3 pt-8 text-muted-foreground"
    >
      <AvatarSilhouette className="h-1/2 w-auto opacity-30" />
      {caption && (
        <p role="status" data-testid={`facelock-picture-caption-${avatar.id}`} className="flex items-center justify-center gap-1 text-center text-xs leading-snug">
          {isPictureInProgress(avatar) && <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden />}
          {caption}
        </p>
      )}
    </div>
  );
}

const ANIMATED_CAPTION: Record<string, string> = {
  drawing: "faceLock.animated.drawing",
  waiting: "faceLock.animated.waiting",
  failed: "faceLock.animated.failed",
  none: "faceLock.animated.none",
};

/**
 * The card's ANIMATED picture: the same portrait in the 3D animated look,
 * drawn with the avatar itself (services/facelock-crew/picture-3d.js), or a
 * silhouette that says why there is none yet. Never an uploaded photo.
 */
export function FaceLockAnimatedPortrait({ avatar }: { avatar: FaceLockAvatar }) {
  const { t } = useTranslation();
  const a = avatar.animated_picture;
  if (a && a.url) {
    return (
      <div className="relative h-full w-full">
        <AuthImg
          src={a.url}
          alt={t("faceLock.animatedAlt", { name: avatar.name })}
          loading="lazy"
          data-testid={`facelock-animated-portrait-${avatar.id}`}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
        {a.state === "drawing" && (
          <p
            role="status"
            data-testid={`facelock-animated-redrawing-${avatar.id}`}
            className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 bg-black/60 px-2 py-1.5 text-center text-xs text-white"
          >
            <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden />
            {t("faceLock.animated.drawingNew")}
          </p>
        )}
      </div>
    );
  }
  const state = a ? a.state : "none";
  const caption = ANIMATED_CAPTION[state] ? t(ANIMATED_CAPTION[state]) : "";
  return (
    <div
      data-testid={`facelock-animated-picture-${avatar.id}`}
      data-picture-state={state}
      className="flex h-full flex-col items-center justify-center gap-2 px-3 pb-3 pt-8 text-muted-foreground"
    >
      <AvatarSilhouette className="h-1/2 w-auto opacity-30" />
      {caption && (
        <p role="status" data-testid={`facelock-animated-caption-${avatar.id}`} className="flex items-center justify-center gap-1 text-center text-xs leading-snug">
          {isAnimatedInProgress(avatar) && <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden />}
          {caption}
        </p>
      )}
    </div>
  );
}

/** The owner-approved guidance, word for word, in the reader's language. */
export function FaceLockGuide() {
  const { t } = useTranslation();
  const rows: Array<[string, string]> = [
    ["faceLock.guide.takeLead", "faceLock.guide.take"],
    ["faceLock.guide.nowLead", "faceLock.guide.now"],
    ["faceLock.guide.avoidLead", "faceLock.guide.avoid"],
    ["faceLock.guide.fastestLead", "faceLock.guide.fastest"],
  ];
  return (
    <div data-testid="facelock-guide" className="grid gap-1.5 rounded-lg border border-border bg-muted/40 p-3 text-sm leading-relaxed">
      <p className="font-semibold text-foreground">{t("faceLock.guide.intro")}</p>
      <ul className="grid gap-1 text-muted-foreground">
        {rows.map(([lead, body]) => (
          <li key={lead}><b className="text-foreground">{t(lead)}</b> {t(body)}</li>
        ))}
      </ul>
      <p className="text-muted-foreground">{t("faceLock.guide.setAside")}</p>
    </div>
  );
}

function objectUrl(f: File): string {
  try {
    return typeof URL.createObjectURL === "function" ? URL.createObjectURL(f) : "";
  } catch {
    return "";
  }
}

/** One big Upload button (one click to the picker), then the thumbnails. */
export function PhotoTray({ files, onAdd, onRemove, disabled, testIdPrefix, uploadLabel, addMoreLabel, full }: {
  files: File[];
  onAdd: (list: FileList | null) => void;
  onRemove: (index: number) => void;
  disabled?: boolean;
  testIdPrefix: string;
  uploadLabel: string;
  addMoreLabel: string;
  /** Hide the button once no more photos fit. */
  full?: boolean;
}) {
  const { t } = useTranslation();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const previews = React.useMemo(() => files.map(objectUrl), [files]);
  React.useEffect(() => () => {
    previews.forEach((u) => { if (u) { try { URL.revokeObjectURL(u); } catch { /* noop */ } } });
  }, [previews]);
  return (
    <div className="grid gap-2">
      <input
        ref={inputRef}
        type="file" data-consent-tool="facelock"
        accept={PHOTO_UPLOAD_ACCEPT}
        multiple
        className="hidden"
        data-testid={`${testIdPrefix}-input`}
        disabled={disabled}
        onChange={(e) => { onAdd(e.target.files); e.currentTarget.value = ""; }}
      />
      {!full && (
        <Button
          type="button"
          size={files.length ? "default" : "lg"}
          variant={files.length ? "outline" : "default"}
          disabled={disabled}
          data-testid={`${testIdPrefix}-upload`}
          onClick={() => inputRef.current?.click()}
          className="w-full sm:w-auto"
        >
          <Upload aria-hidden /> {files.length ? addMoreLabel : uploadLabel}
        </Button>
      )}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-thumbs`}>
          {files.map((f, i) => (
            <div key={`${f.name}_${f.size}_${i}`} className="relative h-16 w-12 overflow-hidden rounded-md border border-border bg-muted">
              {previews[i] ? <img src={previews[i]} alt={f.name} className="h-full w-full object-cover" /> : null}
              {!disabled && (
                <button
                  type="button"
                  aria-label={t("faceLock.create.remove", { name: f.name })}
                  onClick={() => onRemove(i)}
                  className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-black/70 text-white"
                >
                  <X className="h-3 w-3" aria-hidden />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
